import { Box, Button, Grid, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import drv from "../../../../../assets/Images/drv/logo-drv-blanco.png";

const BackgroundCustom = styled(Box)({
  borderRadius: "8px",
  marginBottom: "20px",
  marginTop: "30px",
  backgroundRepeat: "no-repeat !important",
  backgroundPosition: "center !important",
  backgroundSize: "cover !important",
});

const BackgroundDesign = styled("img")({
  borderRadius: "8px",
  maxWidth: "100%",
  marginTop: "15px",
  backgroundRepeat: "no-repeat !important",
  backgroundPosition: "center !important",
  backgroundSize: "cover !important",
});

const Logo = styled("img")({
  height: "40px",
  width: "auto",
  margin: "0px 30px",
  maxWidth: "240px",
  objectFit: "scale-down",
});

type EventTemplateProps = {
  event: any;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export default function EventTemplate({ event }: EventTemplateProps) {
  const { t } = useTranslation(["global"]);

  const types = [
    t("admin.events.webinar"),
    t("admin.events.instagram-live"),
    t("admin.events.live"),
    t("admin.events.training"),
    t("admin.events.fair"),
    t("admin.events.question-session"),
    t("admin.events.launch"),
    t("admin.events.ftf-event"),
  ];

  return (
    <>
      {event.method === 0 ? (
        <BackgroundCustom
          sx={{
            background: event.image
              ? `url(${encodeURI(event.image)})`
              : `${encodeURI(
                  `url(${import.meta.env.VITE_AWS_URL}/brands/${
                    event.brand.background
                  })`
                )}`,
            color: "#ffffff",
          }}
        >
          <Box alignContent="center" pt={2} pb={2}>
            <Typography
              align="center"
              sx={{ fontSize: "15px", color: event.text_color ?? "#000" }}
            >
              {t("you-are-invated-to")} {types[event.type]}:
            </Typography>
          </Box>

          <Box
            pt={2}
            pb={2}
            mb={4}
            sx={{ background: alpha(event.background_color ?? "#ffffff", 0.3) }}
          >
            <Typography
              align="center"
              sx={{
                fontSize: "18px",
                padding: "15px 0px",
                color: event.text_color ?? "#000",
              }}
            >
              {event.title}
            </Typography>
          </Box>

          <Box pt={3} pb={3}>
            <Typography
              align="center"
              sx={{ fontSize: "15px", color: event.text_color ?? "#000" }}
            >
              {event.description}
            </Typography>
          </Box>

          <Box pt={4} pb={1}>
            <Typography
              align="center"
              sx={{ fontSize: "15px", color: event.text_color ?? "#000" }}
            >
              {dayjs(event.date_ini)
                .locale("es")
                .format("dddd DD [de] MMMM [de] YYYY")}
            </Typography>

            <Typography
              align="center"
              sx={{ fontSize: "15px", color: event.text_color ?? "#000" }}
            >
              {event.date_end
                ? `Hora: ${dayjs(event.date_ini).format("hh:mm A")} a ${dayjs(
                    event.date_end
                  ).format("hh:mm A")}`
                : `Hora: ${dayjs(event.date_ini).format("hh:mm A")}`}
            </Typography>
          </Box>

          {event.speaker && (
            <Box pt={1} pb={4}>
              <Typography
                align="center"
                sx={{ fontSize: "15px", color: event.text_color ?? "#000" }}
              >
                {t("speaker")}: <br /> {event.speaker}
              </Typography>
            </Box>
          )}

          {event.link && (
            <Box pt={4} pb={4} textAlign="center">
              <Button
                color="primary"
                variant="outlined"
                component="a"
                target="_blank"
                href={event.link}
                sx={{
                  color: event.text_color ?? "#000",
                  borderColor: event.text_color ?? "#000",
                }}
              >
                {event.text_button ?? t("see-more")}
              </Button>
            </Box>
          )}

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={3}
          >
            <Grid item>
              <Logo src={drv} />
            </Grid>

            <Grid item>
              {event.brand && (
                <Logo
                  src={`${import.meta.env.VITE_AWS_URL}/brands/${
                    event.brand.dark_logo
                  }`}
                  alt={`${event.brand.name} Logo`}
                />
              )}
            </Grid>
          </Grid>
        </BackgroundCustom>
      ) : (
        <BackgroundDesign src={event.image} alt={event.title} />
      )}
    </>
  );
}
